/**
 * The primitives every screen is built from.
 *
 * They exist to hold two invariants in one place rather than in every component
 * that forgets them: nothing interactive is under 44px, and no input is under
 * 16px. Both are enforced again in the base layer of styles.css — this is the
 * belt, that is the braces, and neither is redundant because a `div` with an
 * `onClick` is reached by neither.
 *
 * Colour comes from the semantic tokens (`surface`, `line`, `ink`, `brand`) and
 * never from a `neutral-*` step, so the palette is retuned in one file.
 */
import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
  useId,
} from 'react';

import { cn } from '@/lib/cn.ts';

export type ButtonVariant = 'primary' | 'secondary' | 'quiet';

/**
 * THE HOVER LADDER, which every pressable thing in this app follows.
 *
 * A pointer resting on a control moves it ONE step, and pressing it moves it one
 * further: `surface` → `sunken` → `line` for a pale surface, `brand-600` →
 * `brand-700` → `brand-800` for a filled one, `ink-soft`/`ink-faint` → `ink` for
 * something that is only text. One step, never two — the reader is looking at a
 * photograph of a puppy, and the row under their cursor lighting up like a menu
 * item is chrome asking for attention it does not need.
 *
 * Hover is safe on the phone this app is actually used on because Tailwind 4
 * compiles every `hover:` inside `@media (hover: hover)`, so a touch that lands
 * on a card cannot leave it stuck in its hovered state — which is the bug this
 * whole idea has on a touchscreen. `active:` is what a thumb gets, and it is why
 * the pressed step is still there under every hover step below. **Do not
 * hand-write `:hover` in CSS without that media query** — see the one place that
 * has to, in `styles.css`.
 *
 * `quiet` moves its background and NOT its ink, deliberately: half its callers
 * pass their own colour in `className` — `text-alert` on a delete, `text-brand-ink`
 * on an invite — and a variant that set `hover:text-ink` would turn a red
 * destructive icon grey under the cursor, which is the opposite of what a hover
 * on that button should say.
 *
 * The one thing that costs: a `quiet` button stripped to `px-0` to line its text
 * up with a paragraph has no room for a fill, and the step lands as a 44px band
 * exactly as wide as the words. Those two callers pass `-mx-2 px-2` instead —
 * same text position, and the fill gets an edge to sit inside.
 */
const VARIANTS = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 disabled:bg-brand-300',
  secondary: 'bg-surface text-ink border border-line-strong hover:bg-sunken active:bg-line',
  quiet: 'text-ink-soft hover:bg-sunken active:bg-line',
} as const;

/**
 * The button's look, separated from the button's element.
 *
 * **A control that takes the user somewhere is an `<a>`, never a `<button>`
 * with an onClick.** A link is what gives middle-click, long-press, "open in new
 * tab", a status-bar preview of the destination, and a screen reader announcing
 * "link" rather than "button" — none of which a handler can put back. So when
 * something navigational needs to look like a button, style a router `Link` with
 * this rather than reaching for `<Button>`:
 *
 *     <Link to="/members" className={buttonClasses({ variant: 'secondary' })}>…</Link>
 *
 * `<Button>` is for actions — sign in, save, invite, withdraw. Everything that
 * navigates in this app today is already a `Link`.
 */
export const buttonClasses = ({
  variant = 'primary',
  full = false,
  className,
}: {
  variant?: ButtonVariant | undefined;
  full?: boolean | undefined;
  className?: string | undefined;
} = {}): string =>
  cn(
    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl px-5',
    'text-base font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60',
    VARIANTS[variant],
    full && 'w-full',
    className,
  );

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  full?: boolean;
};

export const Button = ({ variant = 'primary', full = false, className, ...props }: ButtonProps) => (
  <button type="button" {...props} className={buttonClasses({ variant, full, className })} />
);

type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  label: string;
  /**
   * Rendered under the field. An error replaces the hint rather than stacking
   * with it — two lines of guidance under one input is one line too many on a
   * phone, and the error is the one that matters.
   *
   * Explicitly `| undefined`: `exactOptionalPropertyTypes` is on, and every
   * caller here holds its error as `string | null` and passes `?? undefined`.
   */
  hint?: string | undefined;
  error?: string | undefined;
  /**
   * Keeps the label for a screen reader and takes it off the screen.
   *
   * For the one shape this form language has that a visible label would say
   * twice: two inputs sharing one `Field` legend — a date beside the time it
   * happened at. `sr-only` and not a missing label, because "date" and "time"
   * are exactly what somebody navigating by voice needs to tell the two boxes
   * apart.
   */
  labelHidden?: boolean | undefined;
};

export const TextField = ({
  label,
  labelHidden = false,
  hint,
  error,
  className,
  ...props
}: TextFieldProps) => {
  const id = useId();
  const describedBy = `${id}-note`;

  return (
    <div className="flex flex-col gap-1.5">
      <label
        htmlFor={id}
        className={cn('text-sm font-medium text-ink-soft', labelHidden && 'sr-only')}
      >
        {label}
      </label>
      <input
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={error ?? hint ? describedBy : undefined}
        {...props}
        className={cn(
          'w-full rounded-xl border bg-surface px-3.5 text-ink placeholder:text-ink-faint',
          error === undefined ? 'border-line-strong' : 'border-alert',
          className,
        )}
      />
      {(error ?? hint) !== undefined && (
        <p id={describedBy} className={cn('text-sm', error === undefined ? 'text-ink-faint' : 'text-alert')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
};

/**
 * A field for a sentence rather than a value.
 *
 * `TextField`'s twin down to the hint-and-error rule underneath it, and separate
 * from it because the element is: a `<textarea>` wraps, grows and is scrolled,
 * and the two cannot be one component without a prop that switches which tag is
 * rendered — which is the shape that ends up with a `type="date"` textarea.
 *
 * The base layer gives it 16px text but NOT the 44px floor, which it applies to
 * `input` and `select` alone. Three rows clear that several times over, and a
 * minimum height on something the user can drag taller is a floor that fights
 * the handle.
 *
 * `field-sizing-content` lets it grow with what is typed where the browser
 * supports it, so a three-line task does not sit in a box scrolled to its last
 * line; `rows` is the floor underneath and what every other browser uses.
 */
type TextAreaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
};

export const TextArea = ({ label, hint, error, className, rows = 3, ...props }: TextAreaProps) => {
  const id = useId();
  const describedBy = `${id}-note`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink-soft">
        {label}
      </label>
      <textarea
        id={id}
        rows={rows}
        aria-invalid={error !== undefined}
        aria-describedby={error ?? hint ? describedBy : undefined}
        {...props}
        className={cn(
          'w-full resize-y rounded-xl border bg-surface px-3.5 py-2.5 text-ink',
          'field-sizing-content placeholder:text-ink-faint',
          error === undefined ? 'border-line-strong' : 'border-alert',
          className,
        )}
      />
      {(error ?? hint) !== undefined && (
        <p id={describedBy} className={cn('text-sm', error === undefined ? 'text-ink-faint' : 'text-alert')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
};

/**
 * A raised surface. Used for things a person acts *on* — a form, a list of
 * people — and deliberately not for every block on the screen: if everything is
 * a card, the card stops meaning anything and the page becomes a texture.
 */
export const Panel = ({ children, className }: { children: ReactNode; className?: string }) => (
  <section className={cn('rounded-2xl border border-line bg-surface p-5', className)}>
    {children}
  </section>
);

/**
 * A native `<select>`.
 *
 * Native, and not a listbox built out of divs, because the two places this is
 * used — choosing a sire, choosing a dam — are lists of a handful of items on a
 * phone, and the platform's own wheel is better at that than anything that could
 * be written here: it is reachable one-handed, it does not need a scroll
 * container inside a scrolling form, and it is the control the user already
 * knows. The base layer gives it the 44px minimum and the 16px text.
 */
type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
};

export const Select = ({ label, hint, error, className, children, ...props }: SelectProps) => {
  const id = useId();
  const describedBy = `${id}-note`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink-soft">
        {label}
      </label>
      <select
        id={id}
        aria-invalid={error !== undefined}
        aria-describedby={error ?? hint ? describedBy : undefined}
        {...props}
        className={cn(
          'w-full appearance-none rounded-xl border bg-surface px-3.5 pr-10 text-ink',
          // The one form control here that takes a hover: a select is opened by
          // a click, where a text field is typed into. `bg-sunken` sets only
          // the background COLOUR, so the chevron below survives it.
          'transition-colors hover:bg-sunken',
          // The chevron is a background image rather than an icon element,
          // because a `<select>` may not contain one. Encoded inline: an SVG
          // fetched from /public is a request, and this app opens with no radio.
          "bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23787070%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22/%3E%3C/svg%3E')] bg-[length:1.25rem] bg-[position:right_0.75rem_center] bg-no-repeat",
          error === undefined ? 'border-line-strong' : 'border-alert',
          className,
        )}
      >
        {children}
      </select>
      {(error ?? hint) !== undefined && (
        <p id={describedBy} className={cn('text-sm', error === undefined ? 'text-ink-faint' : 'text-alert')}>
          {error ?? hint}
        </p>
      )}
    </div>
  );
};

/**
 * A labelled group of controls that is not one input — a colour picker, a row
 * of photographs, a pair of radio buttons.
 *
 * A `fieldset` and a real `legend`, not a `div` with a bold `span` over it. The
 * difference is only audible: a screen reader announces the legend before every
 * control inside the group, so "collar, red" instead of seven unexplained
 * colours. `TextField` does not use this because a single input has a `label`,
 * which is the better mechanism when there is exactly one thing to point at.
 */
export const Field = ({
  label,
  labelClassName,
  hint,
  error,
  className,
  children,
}: {
  label: string;
  /**
   * Overrides the legend's own type. For the one case where the label is not a
   * quiet field name but the subject of the row — a puppy's name over the strip
   * of photographs taken of it.
   */
  labelClassName?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  className?: string | undefined;
  children: ReactNode;
}) => (
  <fieldset className={cn('flex flex-col gap-1.5', className)}>
    <legend className={cn('mb-1.5 text-sm font-medium text-ink-soft', labelClassName)}>
      {label}
    </legend>
    {children}
    {(error ?? hint) !== undefined && (
      <p className={cn('mt-0.5 text-sm', error === undefined ? 'text-ink-faint' : 'text-alert')}>
        {error ?? hint}
      </p>
    )}
  </fieldset>
);

/**
 * One thing that is either on or off, and says which.
 *
 * A real `<input type="checkbox">` inside its own label rather than a styled
 * `div`: the label makes the whole row a tap target, the input keeps the
 * keyboard and the screen reader's "checkbox, checked", and neither is something
 * a click handler on a box could put back. `min-h-0` undoes the base layer's
 * 44px floor on the input itself — the tap target is the row around it, which
 * clears the minimum, and a 44px-tall checkbox glyph is not a checkbox.
 *
 * The accent colour is the brand red, set on the input so the native control
 * draws its own tick: a hand-drawn tick is one more thing to keep in step with
 * the platform, and this one already matches whatever the OS does with focus.
 */
export const Checkbox = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <label className="flex min-h-tap cursor-pointer items-center gap-2.5 text-sm text-ink-soft transition-colors hover:text-ink">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="size-5 min-h-0 shrink-0 accent-brand-600"
    />
    {label}
  </label>
);

/**
 * One choice from two or three, all of them visible.
 *
 * A segmented control rather than a `<select>` wherever the options are few and
 * the answer is part of what the row *says* — a puppy's sex, a litter's status.
 * The whole point is that the current value is readable without opening
 * anything, which is what a one-handed reader scanning a form needs; a select
 * with three options hides two of them to save a line.
 *
 * Radio inputs under the labels, so arrow keys work and the group is announced
 * as a group. The button look is drawn with `has-checked:`, which is why there
 * is no click handler and no state here.
 */
export type SegmentedOption<T extends string> = { value: T; label: string };

export const Segmented = <T extends string>({
  name,
  value,
  options,
  onChange,
}: {
  name: string;
  /**
   * `null` renders with nothing selected, which is a state this control has to
   * be able to be in: a puppy's sex is required and has no sensible default, and
   * a segmented control that opens on "female" collects "female" from everyone
   * who did not read it.
   */
  value: T | null;
  options: readonly SegmentedOption<T>[];
  onChange: (value: T) => void;
}) => (
  <div className="flex gap-1.5 rounded-xl bg-sunken p-1">
    {options.map((option) => (
      <label
        key={option.value}
        className={cn(
          'flex min-h-tap flex-1 cursor-pointer items-center justify-center rounded-lg px-2',
          'text-center text-sm font-semibold text-ink-soft transition-colors',
          // Ink only, and that is forced: a background step here would land on
          // the same property `has-checked:bg-surface` uses to draw the chosen
          // segment, and the winner between two equal-specificity rules is
          // whichever Tailwind emitted last.
          'hover:text-ink',
          'has-checked:bg-surface has-checked:text-ink has-checked:shadow-sm',
        )}
      >
        <input
          type="radio"
          name={name}
          value={option.value}
          checked={value === option.value}
          // `min-h-0` undoes the base layer's 44px floor for inputs: the tap
          // target here is the label wrapped round it, which clears the
          // minimum, and a visually-hidden input that keeps the floor is a 44px
          // box the clip has to swallow.
          onChange={() => onChange(option.value)}
          className="sr-only min-h-0"
        />
        {option.label}
      </label>
    ))}
  </div>
);
